import { INovaEntity } from './base';
import { formatPhoneNumber } from './js-helpers';

export interface IMessageThread extends INovaEntity {
  orgId: string;
  assetVisitId: string;
  messages?: IMessageThreadMessage[];
  expiresAt: Date;
  usesWhatsApp?: boolean;
}

export enum IMessageStatus {
  DELIVERED = 'delivered',
  FAILED = 'failed',
  SENT = 'sent',
  RECEIVED = 'received'
}

export interface IMessageThreadMessageFile {
  name: string;
  mimeType: AllowedMessageThreadMediaMimeTypes;
  url: string;
  size: number;
}

export enum AllowedMessageThreadMediaMimeTypes {
  JPG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  HEIC = 'image/heic',
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  CSV = 'text/csv',
  TSV = 'text/tab-separated-values'
}

export enum MessageSourceType {
  DRIVER = 'driver',
  WAREHOUSE = 'warehouse',
  SYSTEM = 'system'
}

export interface IMessageThreadMessage {
  providerMessageId: string;
  orgId?: string;
  messageThreadId: string;
  timestamp?: Date;
  content: string;
  files?: IMessageThreadMessageFile[];
  toNumber: string;
  fromNumber: string;
  status: IMessageStatus;
  createdBy?: string;
  sourceType?: MessageSourceType;
}

export interface IMessageThreadEvent {
  orgId: string;
  messageThreadId: string;
  content: string;
}

export function isMessageThreadExpired(messageThread: IMessageThread): boolean {
  const expiresAtUTC = new Date(messageThread.expiresAt).getTime();
  const nowUTC = new Date().getTime();
  return nowUTC > expiresAtUTC;
}

enum SubscriptionKeyword {
  START = 'start',
  RECIBIR = 'recibir',
  RECEBER = 'receber'
}

enum UnsubscriptionKeyword {
  STOP = 'stop',
  ALTO = 'alto',
  PARAR = 'parar'
}

export function isDriverSubscribing(content: string): boolean {
  return Object.values(SubscriptionKeyword).includes(content?.toLowerCase() as SubscriptionKeyword);
}

export function isDriverUnsubscribing(content: string): boolean {
  return Object.values(UnsubscriptionKeyword).includes(
    content?.toLowerCase() as UnsubscriptionKeyword
  );
}

export const BrazilianPhoneNumberLength = 14;

export const SystemMessages = {
  ERROR: {
    DEFAULT: () =>
      'Automated Response: An error occurred while processing your message. Please try again later.',
    INVALID_FILE_TYPE: (fileType?: string) =>
      `Automated Response: Sorry, your message was not delivered. ${
        fileType
          ? `The file type ${fileType} is not supported.`
          : 'This file type is not supported.'
      }`,
    THREAD_NOT_FOUND: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. There are no active conversations with ${warehouseName}.`,
    THREAD_CLOSED: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. Conversation with ${warehouseName} has been closed.`,
    THREAD_EXPIRED: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. Conversation with ${warehouseName} has expired.`
  },
  EVENT: {
    DRIVER_SUBSCRIBED: () => 'The driver has opted to start receiving messages',
    DRIVER_UNSUBSCRIBED: () => 'The driver has opted to stop receiving messages',
    PHONE_CHANGED: (phone: string) => `Driver's phone changed to ${formatPhoneNumber(phone)}`,
    THREAD_OPEN: () => 'Chat resumed by the warehouse',
    THREAD_CLOSED: () => 'Chat ended by the warehouse',
    THREAD_CLOSED_BY_ANOTHER_CHECKIN: (warehouseName: string) =>
      `Chat at ${warehouseName} ended due to another check-in`
  },
  INFO: {
    DRIVER_GREETING: (warehouseName: string, country?: string) => {
      switch (country) {
        case 'BRA':
          return `Obrigado por seu check-in em ${warehouseName}. Instruções serão enviadas para este número. Responda PARAR caso não queira receber mais mensagens.`;
        case 'MEX':
          return `Gracias por registrarte en ${warehouseName}. Las instrucciones serán enviadas a este número. Responde ALTO si no deseas recibir más mensajes.`;
        default:
          return `Thanks for checking in at ${warehouseName}. Instructions will follow to this phone number. Reply STOP to unsubscribe.`;
      }
    },
    DRIVER_INTERACTIVE_GREETING: (warehouseName: string, country?: string) => {
      switch (country) {
        case 'BRA':
          return `Obrigado por seu check-in em ${warehouseName}. Responda OK para receber instruções do armazém. Responda PARAR caso não queira receber mais mensagens.`;
        case 'MEX':
          return `Gracias por registrarte en ${warehouseName}. Responde OK para recibir las instrucciones del almacén. Responde STOP si no deseas recibir más mensajes.`;
        default:
          return `Thanks for checking in at ${warehouseName}. Reply OK to receive instructions from the warehouse. Reply STOP to unsubscribe.`;
      }
    }
  }
};
